<script>
  import { onMount, onDestroy, createEventDispatcher } from "svelte";
  import * as PropTypes from "prop-types";
  const { checkPropTypes, number, bool } = PropTypes;
  export let transitionDuration = 200;
  export let showIndicators = false;
  export let autoplay = false;
  export let delay = 1000;
  export let defaultIndex = 0;

  export let active_item = 0; // readonly
  export let is_vertical = false;
  export let totalItems;
  export let isDragging;

  const propTypes = {
    transitionDuration: number,
    showIndicators: bool,
    autoplay: bool,
    delay: number,
    defaultIndex: number,
    active_item: number,
    is_vertical: bool,
    totalItems: number,
    isDragging: bool,
  };

  let activeIndicator = 0,
    indicators,
    total_elements = 0,
    availableSpace = 0,
    availableWidth = 0,
    swipeElements,
    availableDistance = 0,
    swipeWrapper,
    pos_axis = 0,
    axis,
    longTouch,
    last_axis_pos,
    isMounted = false,
    swipe_parent;

  const page_axis = is_vertical ? "pageY" : "pageX";

  let played = defaultIndex || 0;
  let run_interval = false;
  let swipeInitTimer = false;

  const dispatch = createEventDispatcher();

  function init() {
    swipeElements = swipeWrapper.querySelectorAll(".swipeable-item");
    total_elements = swipeElements.length;
    update();
  }

  $: if (totalItems && isMounted) {
    swipeInitTimer = setTimeout(() => {
      init();
    }, 500);
  }

  function update() {
    const { offsetWidth, offsetHeight } = swipeWrapper;
    availableSpace = is_vertical ? offsetHeight : offsetWidth;
    swipeWrapper.style.transform = generateTranslateValue(
      -1 * availableSpace * activeIndicator
    );

    availableDistance = 0;
    availableWidth = availableSpace * (total_elements - 1);
    if (defaultIndex) {
      changeItem(defaultIndex);
    }
  }

  $: indicators = Array(total_elements);

  $: {
    if (autoplay && !run_interval) {
      run_interval = setInterval(changeView, delay);
    }

    if (!autoplay && run_interval) {
      clearInterval(run_interval);
      run_interval = false;
    }
  }

  // helpers
  function eventDelegate(type) {
    const delegationTypes = {
      add: "addEventListener",
      remove: "removeEventListener",
    };
    if (typeof swipe_parent !== "undefined") {
      swipe_parent[delegationTypes[type]]("mousemove", onMove);
      swipe_parent[delegationTypes[type]]("mouseup", onEnd);
      swipe_parent[delegationTypes[type]]("touchmove", onMove);
      swipe_parent[delegationTypes[type]]("touchend", onEnd);
    }
  }

  function normalizeEventBehavior(e) {
    if (e) e.stopImmediatePropagation();
    if (e) e.stopPropagation();
  }

  function generateTranslateValue(value) {
    return is_vertical
      ? `translate3d(0, ${value}px, 0)`
      : `translate3d(${value}px, 0, 0)`;
  }

  function generateTouchPosCss(value, touch_end = false) {
    const transformString = generateTranslateValue(value);
    const _css = `
      -webkit-transition-duration: ${touch_end ? transitionDuration : "0"}ms;
      transition-duration: ${touch_end ? transitionDuration : "0"}ms;
      -webkit-transform: ${transformString};
      -ms-transform: ${transformString};`;
    return _css;
  }

  onMount(() => {
    checkPropTypes(propTypes, $$props, "prop", "Swipe");
    init();
    isMounted = true;
    if (typeof window !== "undefined") {
      window.addEventListener("resize", update);
    }
  });

  onDestroy(() => {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", update);
    }
    if (swipeInitTimer) {
      clearTimeout(swipeInitTimer);
    }
  });

  let touch_active = false;

  function onMove(e) {
    if (touch_active) {
      normalizeEventBehavior(e);
      if (!isDragging) {
        dispatch("drag", true);
      }
      const _axis = e.touches ? e.touches[0][page_axis] : e[page_axis],
        distance = axis - _axis + pos_axis;

      // if (distance <= availableWidth && distance >= 0) {
      if (distance <= availableWidth) {
        swipeWrapper.style.cssText = generateTouchPosCss(-1 * distance);
        availableDistance = distance;
        last_axis_pos = _axis;

        const direction = axis < last_axis_pos;
        if (direction && e) {
          e.preventDefault();
        }
      }
    }
  }

  function onMoveStart(e) {
    normalizeEventBehavior(e);
    touch_active = true;
    longTouch = false;
    setTimeout(() => {
      longTouch = true;
    }, 250);
    axis = e.touches ? e.touches[0][page_axis] : e[page_axis];
    eventDelegate("add");
  }

  function onEnd(e) {
    if (isDragging) {
      normalizeEventBehavior(e);
      dispatch("drag", false);
    }
    const direction = axis < last_axis_pos;
    if (direction && e) {
      e.preventDefault();
    }
    touch_active = false;
    const _as = availableSpace;
    const accidental_touch =
      Math.round(availableSpace / 50) > Math.abs(axis - last_axis_pos);
    if (longTouch || accidental_touch) {
      availableDistance = Math.round(availableDistance / _as) * _as;
    } else {
      availableDistance = direction
        ? Math.floor(availableDistance / _as) * _as
        : Math.ceil(availableDistance / _as) * _as;
    }

    if (availableDistance < 0) {
      availableDistance = 0;
    }

    axis = null;
    last_axis_pos = null;
    pos_axis = availableDistance;
    activeIndicator = availableDistance / _as;

    swipeWrapper.style.cssText = generateTouchPosCss(
      -1 * _as * activeIndicator,
      true
    );

    active_item = activeIndicator;
    defaultIndex = active_item;
    eventDelegate("remove");
    dispatch("swipe", {
      activeIndex: active_item,
    });
  }

  function changeItem(item) {
    const max = availableSpace;
    availableDistance = max * item;
    activeIndicator = item;
    onEnd();
  }

  function changeView() {
    changeItem(played);
    played = played < total_elements - 1 ? played + 1 : 0;
  }

  export function goTo(step) {
    const item = Math.max(0, Math.min(step, indicators.length - 1));
    changeItem(item);
  }
  export function prevItem() {
    const step = activeIndicator - 1;
    goTo(step);
  }

  export function nextItem() {
    const step = activeIndicator + 1;
    goTo(step);
  }
</script>

<div
  data-testid="swipe-container"
  class="swipe-panel relative overflow-hidden"
  on:touchstart={onMoveStart}
  on:mousedown={onMoveStart}
  bind:this={swipe_parent}
>
  <slot name="head" />

  <div
    class="swipeable-total_elements relative w-[inherit] h-[inherit]"
    bind:this={swipeWrapper}
  >
    <slot name="body" />
  </div>

  {#if showIndicators}
    <div
      class="swipe-indicator swipe-indicator-inside pointer-events-none flex justify-center relative"
    >
      {#each indicators as x, i}
        <span
          class="dot h-xss w-xss bg-transparent rounded-[50%] inline-block cursor-pointer pointer-events-auto mx-3xs {activeIndicator ==
          i
            ? 'is-active'
            : ''}"
          on:click={() => {
            changeItem(i);
          }}
        />
      {/each}
    </div>
  {/if}
</div>

<style>
  .swipe-panel {
    height: var(--sv-swipe-panel-height, 100%);
    width: var(--sv-swipe-panel-width, inherit);
  }

  .swipeable-total_elements {
    z-index: var(--sv-swipe-panel-wrapper-index, 2);
    transition-timing-function: ease-out;
  }

  .swipe-indicator {
    bottom: 1.5rem;
    z-index: var(--sv-swipe-panel-wrapper-index, 2);
  }

  .dot {
    border: 1px solid grey;
  }
  .swipe-indicator .is-active {
    background-color: var(--sv-swipe-indicator-active-color, grey);
  }
</style>
